/**
 * The exercise, inside the session.
 *
 * Two fields, two jobs (SPEC §4.3): the expression in MathLive, and the written
 * justification in plain text — the second one feeds
 * `transversal.redaction-justification`, so it is not decorative and it is not
 * optional-looking.
 *
 * Hints are graded and revealed one at a time, on request. Nothing here
 * computes a correction: no answer is judged on this device (invariant 3).
 */

import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";

import { aEnvoyer, demarrer, releve, visibilite, type Chrono } from "@/child/chrono";
import { queueSubmission, type ServedExercise } from "@/child/use-tutor-session";
import { isPlainNumber, type AnswerSpec } from "@/math/keyboard";
import { NumberAnswerField } from "@/math/NumberAnswerField";
import type { MathAnswer } from "@/math/mathjson";
import { EnonceImages } from "@/ui/EnonceImage";
import { demandeDesFormules } from "@/ui/matieres";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { RedactionField } from "@/math/RedactionField";
import { TexteMathematique } from "@/ui/TexteMathematique";
import { Button, Card } from "@/ui/primitives";

/**
 * MathLive weighs 800 kB. It is fetched when an exercise actually arrives, not
 * during the first paint of a shell that may only be showing today's plan.
 */
const MathAnswerField = lazy(() =>
  import("@/math/MathAnswerField").then((module) => ({ default: module.MathAnswerField })),
);

/**
 * Ce que le serveur a répondu, quand il a répondu quelque chose.
 *
 * `null` veut dire « pas de verdict » — c'est le chemin de la séance, où la
 * réponse part dans le flux du tuteur et où personne ici ne sait si elle est
 * juste.
 */
export function estJuste(verdict: unknown): boolean | null {
  if (verdict !== null && typeof verdict === "object" && "correct" in verdict) {
    const valeur = (verdict as { correct: unknown }).correct;
    if (typeof valeur === "boolean") return valeur;
  }
  return null;
}

/**
 * Ce que devient le panneau une fois la réponse partie.
 *
 * Une seule règle, et c'est elle qui manquait : **on ne ferme l'exercice que
 * lorsqu'il n'y a plus rien à corriger.** Avant le 28 août 2026, le premier
 * envoi passait le champ en lecture seule et remplaçait le bouton par
 * « Envoyé. » ; un enfant qui s'était trompé d'un chiffre n'avait plus aucun
 * moyen de recommencer — signalé par Hedi, sur un exercice de maths, depuis un
 * PC. Deux sorties existaient, « Un autre » et « Changer de sujet », et aucune
 * ne permettait de refaire *celui-là*.
 *
 * Sans verdict — le chemin de la séance, où le tuteur répond dans le flux et où
 * cette page ne sait rien — on gèle comme avant : rien ici ne peut dire si la
 * réponse est juste, et proposer « Réessayer » à un enfant qui a bon serait
 * pire que de ne rien proposer.
 *
 * Le côté serveur est déjà réglé : la deuxième tentative s'enregistre mais
 * n'observe plus la maîtrise, le corrigé étant ouvert depuis la première.
 */
export function apresEnvoi(
  verdict: unknown,
  saisie: string,
): { fige: boolean; dejaJugee: string | null } {
  if (estJuste(verdict) === false) return { fige: false, dejaJugee: saisie };
  return { fige: true, dejaJugee: null };
}

/**
 * Les lettres d'un QCM (`kind: "choix"`), ou `null` pour tout autre exercice.
 *
 * Le serveur n'envoie que les lettres : le texte des propositions est dans
 * l'énoncé, que lisent aussi le tuteur et le parent, et la clé ne quitte jamais
 * le serveur. Une liste vide n'est pas un QCM — des cases sans rien à cocher
 * laisseraient l'enfant devant un bouton qu'il ne peut pas armer.
 */
export function lettresDuQcm(answer: Record<string, unknown>): string[] | null {
  if (answer.kind !== "choix" || !Array.isArray(answer.propositions)) return null;
  const lettres = answer.propositions.filter(
    (lettre): lettre is string => typeof lettre === "string" && lettre.length > 0,
  );
  return lettres.length > 0 ? lettres : null;
}

/**
 * Ce qui part au serveur : les lettres cochées, triées, « A, D ».
 *
 * Triées pour qu'une seconde tentative identique se reconnaisse quel que soit
 * l'ordre des clics — c'est ce que compare `dejaJugee`.
 */
export function reponseDuQcm(cochees: readonly string[]): string {
  return [...new Set(cochees)].sort().join(", ");
}

function readSpec(answer: Record<string, unknown>): AnswerSpec {
  const kind = answer.kind;
  return {
    kind:
      kind === "numeric" || kind === "quantity" || kind === "short_text" || kind === "rubric"
        ? kind
        : "symbolic",
    equivalence: typeof answer.equivalence === "string" ? answer.equivalence : null,
    unit: typeof answer.unit === "string" ? answer.unit : null,
    significant_figures:
      typeof answer.significant_figures === "number" ? answer.significant_figures : null,
    canonical_latex: typeof answer.canonical_latex === "string" ? answer.canonical_latex : null,
    variables: Array.isArray(answer.variables) ? (answer.variables as string[]) : null,
  };
}

export function ExercisePanel({
  exercise,
  hints,
  onSubmit,
  matiere,
}: {
  exercise: ServedExercise;
  hints: string[];
  /**
   * La matière, quand l'appelant la connaît — pour savoir si l'élève écrira des
   * formules dans sa prose.
   *
   * Sans elle, on suppose que oui : c'est ce que faisait la séance, écrite pour
   * les démonstrations de Mehdi. Le défaut se voit sur un exercice de français,
   * où l'on demandait « Écris ta démonstration » avec un bouton « insérer une
   * formule ∑ » — trouvé par Hedi. Une enfant de douze ans à qui l'on demande
   * une démonstration sur un exercice de lecture conclut qu'elle n'a pas compris
   * la consigne, jamais que l'interface s'est trompée de matière.
   */
  matiere?: string;
  /**
   * Ou va la reponse, quand ce n'est pas dans la file d'une seance.
   *
   * La seance de tutorat envoie ses reponses au flux — c'est le defaut, et le
   * seul chemin qui existait. La revision d'avant-rentree n'a pas de seance :
   * elle poste sur `/attempt` et affiche ce que le serveur repond. Le panneau
   * reste le meme des deux cotes, ce qui compte parce que **la saisie
   * mathematique est le risque numero un du projet** : deux panneaux, ce serait
   * deux ergonomies a mesurer, et une seule qui progresse.
   */
  onSubmit?: (reponse: {
    mathjson?: unknown;
    latex?: string | null;
    text?: string | null;
    hints_used?: number;
    duration_ms?: number;
  }) => Promise<unknown>;
}) {
  const spec = useMemo(() => readSpec(exercise.answer), [exercise]);
  const qcm = useMemo(() => lettresDuQcm(exercise.answer), [exercise]);
  const [cochees, setCochees] = useState<string[]>([]);
  const [answer, setAnswer] = useState<MathAnswer>({ latex: "", mathjson: null });
  const [justification, setJustification] = useState("");
  const [revealed, setRevealed] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  /** La saisie déjà jugée fausse : on ne renvoie pas deux fois la même chose. */
  const [dejaJugee, setDejaJugee] = useState<string | null>(null);
  /**
   * Le serveur est en train de lire la réponse — demandé par Hedi le 28 août
   * 2026 : « je veux un loader quand ça interroge le LLM ».
   *
   * Ce n'est pas du confort. Une tentative coûte désormais jusqu'à trois appels
   * modèle — la passe de sincérité, la grille critériée, l'explication de
   * l'erreur — et rien ne bougeait à l'écran pendant ce temps. Un enfant devant
   * un bouton qui ne répond pas conclut que l'envoi a échoué et appuie encore.
   */
  const [envoi, setEnvoi] = useState(false);
  /**
   * Le temps passé, mesuré depuis l'affichage de l'énoncé (`chrono`).
   *
   * Dans une `ref` et non dans un état : rien à l'écran n'en dépend, et un état
   * qui change sans rien redessiner ne sert qu'à provoquer des rendus. L'enfant
   * ne voit aucun compteur — un chronomètre visible transformerait chaque
   * exercice en épreuve chronométrée, ce que la coquille élève s'interdit.
   */
  const chrono = useRef<Chrono>(demarrer(performance.now()));

  // L'exercice change : on repart de zéro. Sans cette remise à l'heure, le
  // panneau réutilisé pour l'exercice suivant lui attribuerait le temps du
  // précédent.
  useEffect(() => {
    chrono.current = demarrer(performance.now());
  }, [exercise.statement, exercise.eventId]);

  // L'onglet passe en arrière-plan : l'horloge s'arrête. C'est la moitié de la
  // mesure — cf. `chrono`, décision 1.
  useEffect(() => {
    const bascule = () => {
      chrono.current = visibilite(
        chrono.current,
        document.visibilityState === "visible",
        performance.now(),
      );
    };
    document.addEventListener("visibilitychange", bascule);
    return () => document.removeEventListener("visibilitychange", bascule);
  }, []);

  const available = [...(exercise.hints ?? []), ...hints];
  // Sans matière connue, on garde le clavier de formules : c'est le comportement
  // de la séance, et la retirer à tort coûterait plus qu'un bouton de trop.
  const avecFormules = matiere === undefined || demandeDesFormules(matiere);
  // Un QCM se coche : ni MathLive, ni champ d'explication — la correction est
  // l'égalité des ensembles de lettres, et une explication n'y serait lue par
  // personne.
  const needsMath = qcm === null && spec.kind !== "short_text" && spec.kind !== "rubric";
  const nombreNu = needsMath && isPlainNumber(spec);
  const saisie = qcm
    ? reponseDuQcm(cochees)
    : `${answer.latex.trim()}\u0000${justification.trim()}`;
  const remplie = qcm
    ? cochees.length > 0
    : needsMath
      ? answer.latex.trim().length > 0
      : justification.trim().length > 0;
  // « Envoyé » veut désormais dire *fini* : l'exercice ne se gèle que lorsqu'il
  // n'y a plus rien à corriger (`apresEnvoi`).
  const canSubmit = remplie && saisie !== dejaJugee;

  const submit = async () => {
    setEnvoi(true);
    try {
      await envoyer();
    } finally {
      // Rendu quoi qu'il arrive : un loader qui reste allumé sur une panne
      // réseau est pire que pas de loader du tout — il ne laisse aucune sortie.
      setEnvoi(false);
    }
  };

  const envoyer = async () => {
    // Relevé **avant** l'appel réseau : ce qu'on veut mesurer est le temps de
    // l'enfant, pas celui du serveur. Une correction qui prend six secondes de
    // modèle n'est pas six secondes de travail.
    const { ms, suivant } = releve(chrono.current, performance.now());
    chrono.current = suivant;

    if (onSubmit && qcm) {
      const verdict = await onSubmit({
        text: reponseDuQcm(cochees),
        hints_used: revealed,
        duration_ms: aEnvoyer(ms),
      });
      const suite = apresEnvoi(verdict, saisie);
      setDejaJugee(suite.dejaJugee);
      setSubmitted(suite.fige);
      return;
    }
    if (onSubmit) {
      const verdict = await onSubmit({
        mathjson: needsMath ? answer.mathjson : undefined,
        latex: needsMath ? answer.latex : null,
        text: justification.trim() || null,
        hints_used: revealed,
        duration_ms: aEnvoyer(ms),
      });
      const suite = apresEnvoi(verdict, saisie);
      setDejaJugee(suite.dejaJugee);
      setSubmitted(suite.fige);
      return;
    }
    const sessionId = sessionStorage.getItem("tuteur.session");
    await queueSubmission(
      sessionId,
      "session.message",
      // En séance, le tuteur reprend ce texte dans `submit_attempt` : le serveur
      // lit les lettres après le deux-points (`exercise.lettres_cochees`).
      qcm ? `Ma réponse : ${reponseDuQcm(cochees)}` : justification.trim() || "Voilà ma réponse.",
      needsMath ? answer : null,
      aEnvoyer(ms),
    );
    setSubmitted(true);
  };

  return (
    <Card title="L'exercice">
      {/* An exercise cut out of a textbook is served as a picture, figure
          included (`exercise` §19 bis): the child reads the image, and its text
          is what the image announces to a screen reader — it is not printed
          next to it. Everything below is unchanged; the picture replaces the
          statement, never the answer. */}
      {exercise.image ? (
        <>
          {/* Au-dessus de l'image et jamais sans elle : nommer un ouvrage
              au-dessus d'une transcription dirait à l'enfant de chercher dans un
              cahier une page qu'il n'a pas sous les yeux. */}
          <EtiquetteParascolaire nom={exercise.parascolaire} />
          <EnonceImages
            images={exercise.images?.length ? exercise.images : [exercise.image]}
            statement={exercise.statement}
          />
        </>
      ) : (
        <TexteMathematique className="statement">{exercise.statement}</TexteMathematique>
      )}

      {/* Un nombre nu ne passe pas par MathLive : le clavier du système est
          meilleur que le nôtre, et il n'y a rien à mettre dans la moitié droite
          d'un pavé de chiffres (`isPlainNumber`). */}
      {needsMath && nombreNu ? (
        <NumberAnswerField
          value={answer.latex}
          onChange={setAnswer}
          label="Écris ta réponse"
          disabled={submitted || envoi}
        />
      ) : null}

      {needsMath && !nombreNu ? (
        <Suspense fallback={<p className="muted">Je prépare le clavier…</p>}>
          <MathAnswerField
            spec={spec}
            value={answer.latex}
            onChange={setAnswer}
            label="Écris ta réponse"
            disabled={submitted || envoi}
          />
        </Suspense>
      ) : null}

      {/* Un exercice jugé à la grille **est** une production rédigée : la zone de
          texte n'y accompagne pas la réponse, elle est la réponse. Et une
          démonstration est de la prose contenant de la notation — mesuré le
          25 août, où le seul outil disponible a produit `Un=3ⁿ+1` avec un exposant
          Unicode. `RedactionField` garde le clavier système pour le texte et
          n'appelle MathLive que pour la formule. */}
      {qcm ? (
        <fieldset className="qcm">
          <legend className="field__label">Coche la ou les propositions exactes</legend>
          {qcm.map((lettre) => (
            <label key={lettre} className="check">
              <input
                type="checkbox"
                checked={cochees.includes(lettre)}
                disabled={submitted || envoi}
                onChange={(event) =>
                  setCochees((avant) =>
                    event.target.checked
                      ? [...avant, lettre]
                      : avant.filter((autre) => autre !== lettre),
                  )
                }
              />
              <span>{lettre}</span>
            </label>
          ))}
        </fieldset>
      ) : spec.kind === "rubric" ? (
        <RedactionField
          value={justification}
          onChange={setJustification}
          disabled={submitted || envoi}
          formules={avecFormules}
          label="Rédige ta réponse"
          placeholder={
            avecFormules
              ? "Écris ta démonstration. Le bouton ci-dessous insère une formule."
              : "Écris ta réponse en quelques phrases."
          }
        />
      ) : (
        <label className="field">
          <span className="field__label">Explique comment tu as fait</span>
          <textarea
            className="textarea"
            rows={3}
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            placeholder="En une ou deux phrases."
            disabled={submitted || envoi}
          />
        </label>
      )}

      {available.length > 0 ? (
        <div className="hints">
          {available.slice(0, revealed).map((hint, index) => (
            <p key={index} className="hint">
              {hint}
            </p>
          ))}
          {revealed < available.length ? (
            <Button variant="quiet" onClick={() => setRevealed((count) => count + 1)}>
              {revealed === 0 ? "Un indice" : "Un autre indice"}
            </Button>
          ) : null}
        </div>
      ) : null}

      {submitted ? (
        <p className="submitted" role="status">
          {onSubmit
            ? "Envoyé."
            : "C'est parti. Le tuteur regarde ta réponse — rien n'est corrigé sur cette tablette."}
        </p>
      ) : (
        <>
          {/* **L'attente se montre dans le bouton, pas à côté.** Une ligne de
              texte grise posée sous un bouton estompé à 45 % — l'état
              `:disabled` — est passée inaperçue de Hedi le 28 août : tout
              s'effaçait en même temps, et rien ne bougeait. C'est le contraire
              de ce qu'un indicateur d'attente doit faire.

              L'indicateur est donc là où l'œil se trouve déjà au moment du
              clic, il garde son contraste (`btn--attente` annule
              l'estompage), et il tourne : sur une tablette, le mouvement est
              le seul signal qui dise « en cours » plutôt que « cassé ». */}
          <Button
            onClick={() => void submit()}
            disabled={!canSubmit || envoi}
            className={envoi ? "btn--attente" : undefined}
          >
            {envoi ? (
              <>
                <span className="spinner" aria-hidden="true" />
                Je regarde ta réponse…
              </>
            ) : dejaJugee === null ? (
              "Envoyer ma réponse"
            ) : (
              "Réessayer"
            )}
          </Button>
          {/* La même chose pour qui ne voit pas l'écran. La région vit en
              permanence et se remplit ensuite : un `role="status"` monté en
              même temps que son texte n'est pas toujours annoncé. */}
          <p className="sr-only" role="status">
            {envoi ? "Je regarde ta réponse…" : ""}
          </p>
        </>
      )}
    </Card>
  );
}
